const USA = 'United States';
const st2Placeholder = '⠀'; // Merlin doesn't accept an empty value and needs this placeholder;

class FormWithValidation extends HTMLElement {
  static checkInputRequired(value, message = 'This field is required.') {
    if (!value) {
      return message;
    }
    return '';
  }

  static checkInputRegex(value, regex, message = `Please enter a valid value.`) {
    if (!value.match(regex)) {
      return message;
    }
    return '';
  }

  static checkInputMin(value, min, message = `At least ${min} characters required.`) {
    if (value.length < min) {
      return message;
    }
    return '';
  }

  static checkInputMax(value, max, message = `Cannot exceed ${max} characters.`) {
    if (value.length > max) {
      return message;
    }
    return '';
  }

  static checkEmail(value, message = `Please enter a valid email.`) {
    if (
      !value.match(
        /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i
      )
    ) {
      return message;
    }
    return '';
  }

  constructor(inputs) {
    super();
    this.submitAttempted = false;
    this.errorClassName = 'field__error_message';
    this.inputs = inputs;
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.connectedCallback = this.connectedCallback.bind(this);
    this.disconnectedCallback = this.disconnectedCallback.bind(this);
  }

  getAllValues() {
    return this.inputs
      .map(([inputName]) => {
        const inputs = Array.from(this.querySelectorAll(`[name="${inputName}"]`));
        if (inputs.length > 1 && inputs[0].type === 'radio') {
          return [inputName, inputs.find((input) => input.checked).value];
        }
        return [inputName, inputs[0].value];
      })
      .reduce((acc, [inputName, inputValue]) => {
        acc[inputName] = inputValue;
        return acc;
      }, {});
  }

  connectedCallback() {
    this.inputs.forEach(([inputName]) => {
      const inputs = Array.from(this.querySelectorAll(`[name="${inputName}"]`));
      inputs.forEach((input) => {
        input.addEventListener('input', this.handleChange);
        input.addEventListener('blur', this.handleChange);
      });
    });
  }

  disconnectedCallback() {
    this.inputs.forEach(([inputName]) => {
      const inputs = Array.from(this.querySelectorAll(`[name="${inputName}"]`));
      inputs.forEach((input) => {
        input.removeEventListener('input', this.handleChange);
        input.removeEventListener('blur', this.handleChange);
      });
    });
    this.resetForm();
  }

  handleSubmit() {
    this.submitAttempted = true;

    this.inputs.forEach(([inputName, validator]) => {
      const input = this.querySelector(`[name="${inputName}"]`);
      this.validateInput(input, validator);
    });
  }

  handleChange(e) {
    if (!this.submitAttempted) {
      return;
    }
    this.validateInput(e.target);
  }

  validateInput(input, validator = this.inputs.find(([inputName]) => inputName === input.name)[1]) {
    const errorMessage = validator(input.value, this.getAllValues());
    const label = input.closest('label');
    let errorEl = label.querySelector(`.${this.errorClassName}`);
    if (!errorEl) {
      errorEl = document.createElement('span');
      errorEl.classList.add(this.errorClassName);
      label.appendChild(errorEl);
    }
    errorEl.innerHTML = errorMessage;
  }

  resetForm() {
    this.inputs.forEach(([inputName, , initialValue]) => {
      const inputs = Array.from(this.querySelectorAll(`[name="${inputName}"]`));
      if (inputs.length > 1 && inputs[0].type === 'radio') {
        inputs.forEach((input) => {
          if (input.value === initialValue) {
            input.setAttribute('checked', '');
          } else {
            input.removeAttribute('checked');
          }
        });
      } else {
        inputs[0].value = initialValue;
      }
    });
  }
}

if (!customElements.get('form-with-validation')) {
  customElements.define('form-with-validation', FormWithValidation);
}

class JournalSubscriptionDeliveryForm extends FormWithValidation {
  constructor() {
    super([
      ['_country', (value) => FormWithValidation.checkInputRequired(value), ''],
      ['_first_name', (value) => FormWithValidation.checkInputRequired(value), ''],
      ['_last_name', (value) => FormWithValidation.checkInputRequired(value), ''],
      ['_street_address_1', (value) => FormWithValidation.checkInputRequired(value), ''],
      ['_street_address_2', () => '', ''],
      ['_city', (value) => FormWithValidation.checkInputRequired(value), ''],
      ['_province', (value) => FormWithValidation.checkInputRequired(value), ''],
      [
        '_postal_code',
        (value, allValues) => {
          let errorMessage = FormWithValidation.checkInputRequired(value);
          if (allValues._country === USA) {
            errorMessage ||= FormWithValidation.checkInputRegex(
              value,
              /^\d{5}(?:[-\s]\d{4})?$/,
              'Please enter a valid zip code.'
            );
          }
          return errorMessage;
        },
        '',
      ],
      ['_is_gift', (value) => FormWithValidation.checkInputRequired(value), 'no'],
      [
        '_gift_recipient_email',
        (value, allValues) => {
          let errorMessage = '';
          if (allValues._is_gift === 'yes') {
            errorMessage = FormWithValidation.checkInputRequired(value);
            errorMessage ||= FormWithValidation.checkEmail(value);
          }
          return errorMessage;
        },
        '',
      ],
      [
        '_gift_sender_name',
        (value, allValues) => {
          let errorMessage = '';
          if (allValues._is_gift === 'yes') {
            errorMessage = FormWithValidation.checkInputRequired(value);
          }
          return errorMessage;
        },
        '',
      ],
      [
        '_gift_message',
        (value, allValues) => {
          let errorMessage = '';
          if (allValues._is_gift === 'yes') {
            errorMessage = FormWithValidation.checkInputRequired(value);
          }
          return errorMessage;
        },
        '',
      ],
    ]);

    this.countrySelector = '#JournalDelivery__Country';
    this.stateInputId = 'JournalDelivery__State';
    this.stateSelector = `#${this.stateInputId}`;
  }

  connectedCallback() {
    super.connectedCallback();

    this.getProductForm()?.registerAdditionalPropertyGetters(this.handleSubmit);

    try {
      this.statesByCountry = JSON.parse(this.dataset.statesByCountry);
      this.countries = JSON.parse(this.dataset.countries);
      this.geocodingApi = JSON.parse(this.dataset.geocodingApi);
    } catch (err) {
      this.countries = [];
      this.statesByCountry = {};
      this.geocodingApi = {};
    }

    this.populateCountryOptions();

    this.querySelector(this.countrySelector).addEventListener('input', this.handleCountryChange);

    const modal = this.getModal();

    ['entered-address', 'suggested-address'].forEach((inputId) => {
      const input = modal.querySelector(`#${inputId}`);
      input.addEventListener('change', this.onSelectedAddressUpdate);
    });

    const proceedCta = modal.querySelector('#JournalDeliveryVerification__proceed');
    proceedCta.addEventListener('click', this.addToCart);
    const editCta = modal.querySelector('#JournalDeliveryVerification__edit');
    editCta.addEventListener('click', this.editAddress);

    this.cartUpdateSubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.getProductForm()?.unregisterAdditionalPropertyGetters(this.handleSubmit);

    this.querySelector(this.countrySelector).removeEventListener('input', this.handleCountryChange);

    const modal = this.getModal();
    const proceedCta = modal.querySelector('#JournalDeliveryVerification__proceed');
    proceedCta.removeEventListener('click', this.addToCart);
    const editCta = modal.querySelector('#JournalDeliveryVerification__edit');
    editCta.removeEventListener('click', this.editAddress);

    this.cartUpdateSubscriber?.();
  }

  handleSubmit = async () => {
    super.handleSubmit();

    if (this.querySelector(`.${this.errorClassName}:not(:empty)`)) {
      throw new Error(''); // No message to display; inputs already show error messages
    }

    const formValues = this.getAllValues();

    const { userEntered, suggested, userEnteredFound } = await this.verifyAddress(formValues);

    if (userEnteredFound) {
      return this.getFinalProperties(formValues);
    }

    const modal = this.getModal();

    const heading = modal.querySelector('.base-modal__heading');
    heading.innerHTML = suggested ? 'Please select shipping address' : 'Is this shipping address correct?';

    const subheading = modal.querySelector('.base-modal__subheading');
    subheading.innerHTML = suggested
      ? 'To ensure prompt and accurate delivery, we suggest a modified shipping address.'
      : '';

    const suggestedOption = modal.querySelector('.journal-delivery-modal__suggested-address');
    suggestedOption.classList[suggested ? 'remove' : 'add']('visually-hidden');

    const enteredOption = modal.querySelector('.journal-delivery-modal__entered-address');

    [
      [suggested, suggestedOption],
      [userEntered, enteredOption],
    ].forEach(([data, element]) => {
      const valueWrapper = element.querySelector('.address-label-value');
      valueWrapper.innerHTML = '';
      if (data) {
        const input = element.querySelector('input');
        input.dataset.additionalProperties = JSON.stringify(data.formValues);

        if ((suggested && data === suggested) || (!suggested && data === userEntered)) {
          input.setAttribute('checked', '');
        } else {
          input.removeAttribute('checked');
        }
        const event = new Event('change', { bubbles: true });
        input.dispatchEvent(event);

        valueWrapper.append(data.line1, '\n', data.line2, '\n', data.line3);
      }
    });

    modal.show(undefined, { preventScroll: true });

    throw new Error(''); // No message to display; modal will prompt user to make corrections
  };

  editAddress = () => {
    const modal = this.getModal();
    modal.hide();
  };

  addToCart = (e) => {
    const modal = this.getModal();
    modal.hide();

    const additionalProperties = JSON.parse(modal.querySelector('input:checked').dataset.additionalProperties);

    this.getProductForm().submit(e, {
      runAdditionalHandlers: false,
      additionalProperties,
    });
  };

  onCartUpdate = () => {
    this.resetForm();
  };

  onSelectedAddressUpdate = (e) => {
    if (e.target.checked) {
      const additionalProperties = JSON.parse(e.target.dataset.additionalProperties);
      this.updateDeliveryVariant(additionalProperties._country);
    }
  };

  getProductForm() {
    return this.closest('section').querySelector('product-form');
  }

  getModal() {
    return document.querySelector('#PopupModal-JournalDeliveryVerification');
  }

  populateCountryOptions() {
    const options = this.countries.map((country) => {
      const option = document.createElement('option');
      option.value = country;
      option.label = country;
      return option;
    });
    this.querySelector(this.countrySelector)?.append(...options);
  }

  handleCountryChange = (e) => {
    const newCountry = e.target.value;
    const newStates = this.statesByCountry[newCountry];

    if (newStates?.length) {
      // Should be a dropdown to select from these states
      const options = newStates.map((state) => {
        const option = document.createElement('option');
        option.value = state;
        option.label = state;
        return option;
      });
      if (this.querySelector(this.stateSelector)?.tagName === 'INPUT') {
        const selectInput = document.createElement('select');
        selectInput.name = '_province';
        selectInput.id = this.stateInputId;
        selectInput.classList.add('select__select');
        selectInput.addEventListener('input', this.handleChange);
        selectInput.addEventListener('blur', this.handleChange);
        this.querySelector(this.stateSelector).replaceWith(selectInput);
      }
      this.querySelector(this.stateSelector)?.replaceChildren(...options);
    } else if (this.querySelector(this.stateSelector)?.tagName === 'SELECT') {
      // Otherwise allow free-form input
      const textInput = document.createElement('input');
      textInput.type = 'text';
      textInput.name = '_province';
      textInput.id = this.stateInputId;
      textInput.classList.add('field__input');
      textInput.addEventListener('input', this.handleChange);
      textInput.addEventListener('blur', this.handleChange);
      this.querySelector(this.stateSelector).replaceWith(textInput);
    }

    this.updateDeliveryVariant(newCountry);
  };

  updateDeliveryVariant(newCountry) {
    // Update selected variant for new pricing, etc
    const variantSelectsEl = this.closest('section').querySelector('variant-selects');
    const selectedDeliveryName = newCountry === USA ? 'Domestic' : 'International';

    const inputs = Array.from(variantSelectsEl.querySelectorAll('input[name^="Delivery-2"]'));
    let checkedInput;
    inputs.forEach((input) => {
      if (input.value === selectedDeliveryName) {
        input.setAttribute('checked', '');
        checkedInput = input;
      } else {
        input.removeAttribute('checked');
      }
    });
    const event = new Event('change', { bubbles: true });
    checkedInput.dispatchEvent(event);
  }

  async verifyAddress(formValues) {
    const userEntered = this.formatFinalAddress('You Entered', formValues);

    try {
      const requestUrl = new URL(`${this.geocodingApi.url}`);
      requestUrl.searchParams.set('access_token', this.geocodingApi.accessToken);
      requestUrl.searchParams.set('type', 'address');
      requestUrl.searchParams.set('limit', '1');
      requestUrl.searchParams.set('language', 'en');
      requestUrl.searchParams.set('address_line1', formValues._street_address_1);
      requestUrl.searchParams.set('place', formValues._city);
      requestUrl.searchParams.set('region', formValues._province);
      requestUrl.searchParams.set('postcode', formValues._postal_code);
      requestUrl.searchParams.set('country', formValues._country);
      const result = (await (await fetch(requestUrl.toString())).json()).features[0].properties;

      const { context: suggested, match_code } = result;

      suggested.region.name = this.mapResultState(suggested.region.name);

      const formattedSuggested = this.formatFinalAddress('We Found', {
        ...formValues,
        _street_address_1:
          match_code.address_number === 'matched' && match_code.street === 'matched'
            ? formValues._street_address_1
            : suggested.address.name,
        _city: match_code.place === 'matched' ? formValues._city : suggested.place.name,
        _province: match_code.region === 'matched' ? formValues._province : suggested.region.name,
        _postal_code: match_code.postcode === 'matched' ? formValues._postal_code : suggested.postcode.name,
        _country: match_code.country === 'matched' ? formValues._country : suggested.country.name,
      });

      if (
        formattedSuggested.line1 === userEntered.line1 &&
        formattedSuggested.line2 === userEntered.line2 &&
        formattedSuggested.line3 === userEntered.line3
      ) {
        return {
          userEntered,
          suggested: null,
          userEnteredFound: true,
        };
      }

      return {
        userEntered,
        suggested: formattedSuggested,
        userEnteredFound: false,
      };
    } catch (err) {
      return {
        userEntered,
        suggested: null,
        userEnteredFound: false,
      };
    }
  }

  mapResultState(state) {
    switch (state) {
      case 'District of Columbia': {
        return 'Washington DC';
      }
      default: {
        return state;
      }
    }
  }

  parseResultZip(country, postalCode) {
    const alphaNumericPostalCode = postalCode.replace(/[^a-zA-Z0-9]/g, '');

    switch (country) {
      case [USA]: {
        return alphaNumericPostalCode.slice(0, 5);
      }
      case 'Canada': {
        return alphaNumericPostalCode.slice(0, 6);
      }
      default: {
        return alphaNumericPostalCode.slice(0, 8);
      }
    }
  }

  formatFinalAddress(heading, formValues) {
    const { _street_address_1, _street_address_2, _city, _province, _postal_code, _country } = formValues;
    return {
      heading,
      line1: [_street_address_1, _street_address_2].filter(Boolean).join(' '),
      line2: `${_city}, ${_province} ${_postal_code}`,
      line3: _country,
      formValues: this.getFinalProperties(formValues),
    };
  }

  getFinalProperties({
    _first_name,
    _last_name,
    _street_address_1,
    _street_address_2,
    _city,
    _province,
    _postal_code,
    _country,
    _is_gift,
    _gift_recipient_email,
    _gift_sender_name,
    _gift_message,
  }) {
    return {
      _first_name,
      _last_name,
      _street_address_1,
      _street_address_2: _street_address_2 || st2Placeholder,
      _city,
      _province,
      _postal_code: this.parseResultZip(_country, _postal_code),
      _country,
      _is_gift,
      ...(_is_gift === 'yes' && { _gift_recipient_email, _gift_sender_name, _gift_message }),
    };
  }
}

if (!customElements.get('journal-subscription-delivery-form')) {
  customElements.define('journal-subscription-delivery-form', JournalSubscriptionDeliveryForm);
}
